import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  FaEnvelope,
  FaInstagram,
  FaRocket,
  FaSatellite,
  FaSpaceShuttle
} from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import SEO from '../SEO';
import { buildStructuredData, getAbsoluteUrl, getKeywords, getRouteSeo, siteSeo } from '../../seo/seoUtils';
import './Contact.css';

const services = [
  {
    icon: FaSpaceShuttle,
    title: 'Launch planning',
    description: 'Mission architecture and deployment planning shaped around your payload and timeline.'
  },
  {
    icon: FaRocket,
    title: 'Vehicle support',
    description: 'Technical guidance for launch readiness, validation, and integration milestones.'
  },
  {
    icon: FaSatellite,
    title: 'Satellite programs',
    description: 'End-to-end support for spacecraft design, deployment, and operational follow-through.'
  }
];

const contactInfo = [
  {
    icon: FaEnvelope,
    title: 'Email',
    info: 'contact@antaral.space',
    link: 'mailto:contact@antaral.space'
  }
];

const introVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1] }
  }
};

const layoutVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15, delayChildren: 0.1 }
  }
};

const panelVariants = {
  hidden: { opacity: 0, y: 32 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] }
  }
};

const Contact = () => {
  const seo = getRouteSeo('/contact');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [submitStatus, setSubmitStatus] = useState('');

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    const subject = encodeURIComponent(formData.subject || 'Mission enquiry');
    const body = encodeURIComponent(
      `Name: ${formData.name}\nEmail: ${formData.email}\n\n${formData.message}`
    );

    window.location.href = `mailto:contact@antaral.space?subject=${subject}&body=${body}`;
    setSubmitStatus('success');
  };

  return (
    <>
      <SEO
        title={seo.title}
        description={seo.description}
        keywords={getKeywords(seo)}
        url={getAbsoluteUrl('/contact')}
        image={siteSeo.image}
        imageAlt={siteSeo.imageAlt}
        structuredData={buildStructuredData('/contact')}
      />

      <section id="contact" className="contact-section">
        <div className="contact-section__container">
          <motion.div
            className="contact-section__intro"
            variants={introVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
          >
            <motion.span className="section-kicker" variants={fadeUp}>Get in touch</motion.span>
            <motion.h1 className="section-heading" variants={fadeUp}>
              Ready to build your <span>next space program</span>?
            </motion.h1>
            <motion.p className="section-copy" variants={fadeUp}>
              Tell us what you are launching, researching, or building. We can help shape the right
              mission path, vehicle strategy, and systems support.
            </motion.p>
          </motion.div>

          <motion.div
            className="contact-section__layout"
            variants={layoutVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-50px' }}
          >
            <motion.div className="contact-section__form-card glass-card" variants={panelVariants}>
              <div className="contact-section__form-header">
                <h3>Send us a message</h3>
                <p>Share your project scope and we will get back to you with the right next step.</p>
              </div>

              {submitStatus === 'success' && (
                <motion.div
                  className="contact-section__success"
                  role="status"
                  aria-live="polite"
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, ease: 'easeOut' }}
                >
                  <span>
                    Opening your email app to send to{' '}
                    <a href="mailto:contact@antaral.space">contact@antaral.space</a>.
                    If nothing opens, email us directly.
                  </span>
                </motion.div>
              )}

              <form onSubmit={handleSubmit} className="contact-section__form">
                <div className="contact-section__row">
                  <label className="contact-section__field">
                    <span>Full name</span>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Enter your full name"
                      required
                    />
                  </label>

                  <label className="contact-section__field">
                    <span>Email address</span>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="Enter your email"
                      required
                    />
                  </label>
                </div>

                <label className="contact-section__field">
                  <span>Subject</span>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    placeholder="How can we help?"
                    required
                  />
                </label>

                <label className="contact-section__field">
                  <span>Message</span>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows="6"
                    placeholder="Tell us about your mission, payload, timeline, or partnership goals."
                    required
                  ></textarea>
                </label>

                <button type="submit" className="btn btn-primary contact-section__submit">
                  Send via email
                </button>
              </form>
            </motion.div>

            <motion.div className="contact-section__aside" variants={panelVariants}>
              <div className="contact-section__info glass-card">
                <h3>Contact details</h3>
                <div className="contact-section__details">
                  {contactInfo.map((item) => {
                    const IconComponent = item.icon;
                    const content = (
                      <div className="contact-section__detail">
                        <div className="contact-section__detail-icon">
                          <IconComponent aria-hidden="true" />
                        </div>
                        <div>
                          <strong>{item.title}</strong>
                          <span>{item.info}</span>
                        </div>
                      </div>
                    );

                    return item.link ? (
                      <a key={item.title} href={item.link} className="contact-section__detail-link">
                        {content}
                      </a>
                    ) : (
                      <div key={item.title}>{content}</div>
                    );
                  })}
                </div>

                <div className="contact-section__socials">
                  <a href="https://x.com/AntaralAero" target="_blank" rel="noopener noreferrer" aria-label="Follow on X">
                    <FaXTwitter aria-hidden="true" />
                    <span>X</span>
                  </a>
                  <a href="https://www.instagram.com/antaralaerospace" target="_blank" rel="noopener noreferrer" aria-label="Follow on Instagram">
                    <FaInstagram aria-hidden="true" />
                    <span>Instagram</span>
                  </a>
                </div>
              </div>

              <div className="contact-section__services glass-card">
                <h3>What we can support</h3>
                <div className="contact-section__service-list">
                  {services.map((service) => {
                    const IconComponent = service.icon;
                    return (
                      <article key={service.title} className="contact-section__service">
                        <div className="contact-section__service-icon">
                          <IconComponent aria-hidden="true" />
                        </div>
                        <div>
                          <strong>{service.title}</strong>
                          <p>{service.description}</p>
                        </div>
                      </article>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default Contact;
